import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Save, FolderOpen, Trash2 } from "lucide-react";
import PageHeader from "@/components/ui/PageHeader";
import { Button } from "@/components/ui/button";
import IconProvider from "@/components/ui/IconProvider";
import { useStringsDataContext } from "@/providers/StringsDataProvider";
import { useSimplifiedUserData } from "@/providers/SimplifiedUserDataProvider";
import { useUIElementsDataContext } from "@/providers/UIElementsDataProvider";
import { useTheme } from "@/providers/ThemeProvider";
import { apiService } from "@/lib/api-functions/api-service";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/DefaultSelectDropdown";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/InputDefault";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import OverflowReportPanel from "@/components/ui/OverflowReportPanel";
import { usePageAccessGuard } from '@/hooks/usePageAccessGuard';
import {
  checkAllLanguagesForOverflow,
  highlightOverflowElements,
  OverflowReport,
} from "@/lib/utils/overflowDetection";
import Editor from "react-simple-code-editor";
import { highlight, languages } from "prismjs";
import "prismjs/components/prism-markup";
import "prismjs/components/prism-css";
import "@/styles/prism-theme.css";
import { UnityExportDialog } from "@/components/ui/UnityExportDialog";

import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
interface UITemplate {
  id: string;
  name: string;
  html: string;
  css: string;
  gameId: string;
  createdAt: string;
  updatedAt: string;
}

interface ImageData {
  id: string;
  name: string;
  imagekey: string;
  url: string;
  path: string;
}

export default function UIPage() {
  usePageAccessGuard('ui');
  const { t } = usePlatformTranslation();
  const { selectedGame } = useSimplifiedUserData();
  const { effectiveTheme } = useTheme();
  const stringsContext = useStringsDataContext();
  const strings = useMemo(() => stringsContext?.stringsItems || [], [stringsContext?.stringsItems]);
  const { toast } = useToast();
  const { uiElements, createUIElement, updateUIElement, deleteUIElement, isLoading: uiElementsLoading } = useUIElementsDataContext();
  
  // Images state
  const [images, setImages] = useState<ImageData[]>([]);
  
  // Debug logging (can be removed in production)
  // console.log('UIPage render - selectedGame:', selectedGame);
  // console.log('UIPage render - strings:', strings.length);
  // console.log('UIPage render - stringsContext:', stringsContext);
  
  // Get available languages from game settings
  const defaultLanguage = selectedGame?.settings?.defaultLanguage || "en";
  
  // Add "Source Text" as the first option
  // Map languages to ensure consistent structure
  const languageOptions = useMemo(() => {
    const languages = selectedGame?.settings?.languages || [];
    const langArray = Array.isArray(languages) ? languages : [];
    const mappedLanguages = langArray.map(lang => ({
      code: typeof lang === 'string' ? lang : lang.code,
      name: typeof lang === 'string' ? lang : (lang.name || lang.code)
    }));
    
    return [
      { code: 'source', name: 'Source Text' },
      ...mappedLanguages
    ];
  }, [selectedGame?.settings?.languages]);
  
  const [selectedLanguage, setSelectedLanguage] = useState('source');
  
  // Template management state
  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false);
  const [isLoadDialogOpen, setIsLoadDialogOpen] = useState(false);
  const [isUnityExportDialogOpen, setIsUnityExportDialogOpen] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null);
  const [editingTemplateName, setEditingTemplateName] = useState<string>("");
  
  const [html, setHtml] = useState(`<button class="btn">Click Me</button>`);
  
  const [css, setCss] = useState(`.btn {
  width: 130px;
  height: 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  font-size: 16px;
  border-radius: 8px;
  cursor: pointer;
  transition: transform 0.2s;
}

.btn:hover {
  transform: scale(1.05);
}`);

  const [previewContent, setPreviewContent] = useState("");
  const [iframeError, setIframeError] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  
  // Overflow detection state
  const [overflowReport, setOverflowReport] = useState<OverflowReport | null>(null);
  const [isCheckingOverflow, setIsCheckingOverflow] = useState(false);
  const [showSizeBoxes, setShowSizeBoxes] = useState(false);

  // Track if initial load has happened
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false);

  // Reset size boxes when preview content changes
  useEffect(() => {
    setShowSizeBoxes(false);
  }, [previewContent]);

  // UI elements are loaded automatically by the provider

  // Auto-load initial UI element on mount or when UI elements change
  useEffect(() => {
    // Skip if already loaded or if UI elements are still loading
    if (hasInitiallyLoaded || uiElementsLoading) return;

    // If we have saved UI elements, load the first one (most recent)
    if (uiElements.length > 0) {
      const firstElement = uiElements[0];
      setHtml(firstElement.html);
      setCss(firstElement.css);
      setEditingTemplateId(firstElement.id);
      setEditingTemplateName(firstElement.name);
      setHasInitiallyLoaded(true);
    } else if (!uiElementsLoading && uiElements.length === 0) {
      // No saved elements, set default centered button
      setHtml(`<div class="container">
  <button class="btn">Click Me</button>
</div>`);
      setCss(`.container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
}

.btn {
  width: 200px;
  height: 40px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  font-size: 16px;
  border-radius: 8px;
  cursor: pointer;
  transition: transform 0.2s;
}

.btn:hover {
  transform: scale(1.05);
}`);
      setEditingTemplateId(null);
      setEditingTemplateName("");
      setHasInitiallyLoaded(true);
    }
  }, [uiElements, uiElementsLoading, hasInitiallyLoaded]);

  // Reset initial load flag when game changes
  useEffect(() => {
    setHasInitiallyLoaded(false);
  }, [selectedGame?.id]);

  // Fetch images for the selected game
  useEffect(() => {
    const fetchImages = async () => {
      if (!selectedGame?.id) {
        setImages([]);
        return;
      }

      try {
        const response = await apiService.listImages(selectedGame.id);
        if (response.success && response.data) {
          const imageData: ImageData[] = response.data.images.map((img: any) => ({
            id: img.id,
            name: img.name,
            imagekey: img.imagekey,
            url: img.url,
            path: img.path,
          }));
          setImages(imageData);
        }
      } catch (error) {
        console.error("Failed to fetch images:", error);
        setImages([]);
      }
    };

    fetchImages();
  }, [selectedGame?.id]);

  // Quick save - updates existing template without dialog
  const handleQuickSave = useCallback(async () => {
    if (!selectedGame?.id) {
      toast({
        title: "Error",
        description: "Please select a game first",
        variant: "destructive",
      });
      return;
    }

    if (!editingTemplateId || !editingTemplateName) {
      // No template loaded, open dialog for new save
      setTemplateName("");
      setIsSaveDialogOpen(true);
      return;
    }

    try {
      await updateUIElement({
        id: editingTemplateId,
        game_id: selectedGame.id,
        name: editingTemplateName,
        html,
        css,
      });
      toast({
        title: "Template updated",
        description: `"${editingTemplateName}" has been updated successfully`,
      });
    } catch (error) {
      console.error("Failed to update template:", error);
      // Error toast is already shown by the store
    }
  }, [selectedGame, editingTemplateId, editingTemplateName, html, css, updateUIElement, toast]);

  // Save as - opens dialog to save as new or update with new name
  const handleSaveAs = useCallback(() => {
    if (!selectedGame?.id) {
      toast({
        title: "Error",
        description: "Please select a game first",
        variant: "destructive",
      });
      return;
    }
    
    // Pre-fill with current template name if editing
    setTemplateName(editingTemplateName || "");
    setIsSaveDialogOpen(true);
  }, [selectedGame, editingTemplateName, toast]);

  // Save template from dialog
  const saveTemplate = useCallback(async () => {
    if (!selectedGame?.id) {
      toast({
        title: "Error",
        description: "Please select a game first",
        variant: "destructive",
      });
      return;
    }

    if (!templateName.trim()) {
      toast({
        title: "Error",
        description: "Please enter a template name",
        variant: "destructive",
      });
      return;
    }

    try {
      // Check if saving with the same name as currently loaded template
      const isSameName = editingTemplateId && templateName.trim() === editingTemplateName;
      
      if (isSameName) {
        // Update existing template
        await updateUIElement({
          id: editingTemplateId,
          game_id: selectedGame.id,
          name: templateName.trim(),
          html,
          css,
        });
        toast({
          title: "Template updated",
          description: `"${templateName}" has been updated successfully`,
        });
        setEditingTemplateName(templateName.trim());
      } else {
        // Create new template
        const newElement = await createUIElement({
          game_id: selectedGame.id,
          name: templateName.trim(),
          html,
          css,
        });
        toast({
          title: "Template saved",
          description: `"${templateName}" has been saved successfully`,
        });
        // Set as currently editing template
        setEditingTemplateId(newElement.id);
        setEditingTemplateName(newElement.name);
      }

      setTemplateName("");
      setIsSaveDialogOpen(false);
    } catch (error) {
      console.error("Failed to save template:", error);
      // Error toast is already shown by the store
    }
  }, [selectedGame, templateName, html, css, editingTemplateId, editingTemplateName, createUIElement, updateUIElement, toast]);

  // Load template
  const loadTemplate = useCallback((element: {id: string; name: string; html: string; css: string}) => {
    setHtml(element.html);
    setCss(element.css);
    setEditingTemplateId(element.id);
    setEditingTemplateName(element.name);
    setIsLoadDialogOpen(false);
    
    toast({
      title: "Template loaded",
      description: `"${element.name}" has been loaded`,
    });
  }, [toast]);

  // Delete template
  const deleteTemplate = useCallback(async (templateId: string) => {
    if (!selectedGame?.id) return;

    try {
      await deleteUIElement(templateId, selectedGame.id);
      // Success toast is shown by the store
    } catch (error) {
      console.error("Failed to delete template:", error);
      // Error toast is already shown by the store
    }
  }, [selectedGame, deleteUIElement]);

  // Function to replace LocaKeys with translated text
  const replaceLocaKeys = useCallback((htmlContent: string, languageCode: string): string => {
    // Match patterns like {{key}} or {key}
    return htmlContent.replace(/\{\{?([a-zA-Z0-9_.-]+)\}\}?/g, (match, locaKey) => {
      // Find the string item by abbrev (LocaKey)
      const stringItem = strings.find(item => item.abbrev === locaKey);
      
      if (!stringItem) {
        // If not found, return the original match with a warning indicator
        return `<span style="color: red; font-weight: bold;" title={t('platform.pages.uipage.locakey_not_found')}>${match}</span>`;
      }
      
      // If source text is selected, return source_text
      if (languageCode === 'source') {
        return stringItem.source_text || match;
      }
      
      // Find the translation for the selected language
      const translation = stringItem.translations?.find(
        trans => trans.language_code === languageCode
      );
      
      if (translation) {
        return translation.translated_text || stringItem.source_text || match;
      }
      
      // Fallback to source text if translation not found
      return stringItem.source_text || match;
    });
  }, [strings]);

  // Function to replace Image Keys with URLs
  const replaceImageKeys = useCallback((htmlContent: string): string => {
    // Match patterns like {{img:key}}
    return htmlContent.replace(/\{\{img:([a-z0-9_-]+)\}\}/g, (match, imageKey) => {
      // Find the image by imagekey
      const image = images.find(img => img.imagekey === imageKey);
      
      if (!image) {
        // If not found, return the original match with a warning indicator
        return `<span style="color: orange; font-weight: bold;" title={t('platform.pages.uipage.image_key_not_found_imagekey')}>${match}</span>`;
      }
      
      // Return the actual URL
      return image.url;
    });
  }, [images]);

  useEffect(() => {
    // Replace Image Keys first, then LocaKeys in HTML based on selected language
    let processedHtml = replaceImageKeys(html);
    processedHtml = replaceLocaKeys(processedHtml, selectedLanguage);
    
    const content = `
      <!DOCTYPE html>
      <html>
        <head>
          <style>
            body {
              margin: 0;
              padding: 20px;
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
            }
            ${css}
          </style>
        </head>
        <body>
          ${processedHtml}
        </body>
      </html>
    `;
    
    // console.log('Generated preview content:', content);
    setPreviewContent(content);
    setIframeError(false); // Reset error state when content changes
  }, [html, css, selectedLanguage, replaceLocaKeys, replaceImageKeys]);
  
  // Update highlights when report or language changes
  useEffect(() => {
    if (overflowReport && iframeRef.current?.contentDocument) {
      // Wait for iframe to be fully loaded
      const iframe = iframeRef.current;
      const applyHighlights = () => {
        if (iframe.contentDocument) {
          highlightOverflowElements(
            iframe.contentDocument,
            overflowReport.issues,
            selectedLanguage
          );
        }
      };
      
      // Apply highlights after a short delay to ensure content is rendered
      const timer = setTimeout(applyHighlights, 100);
      return () => clearTimeout(timer);
    }
  }, [overflowReport, selectedLanguage]);
  
  // Check all languages for overflow
  const handleCheckOverflow = useCallback(async () => {
    const iframe = iframeRef.current;
    
    if (!iframe) {
      toast({
        title: "Error",
        description: "Preview not ready. Please wait for the preview to load.",
        variant: "destructive",
      });
      return;
    }
    
    setIsCheckingOverflow(true);
    
    try {
      // Wait for iframe to be accessible with proper polling
      await new Promise<void>((resolve, reject) => {
        let attempts = 0;
        const maxAttempts = 50; // 5 seconds max (50 * 100ms)
        
        const checkIframeReady = () => {
          attempts++;
          
          // Check if we can access contentDocument
          const doc = iframe.contentDocument || iframe.contentWindow?.document;
          
          if (doc && doc.body && doc.body.innerHTML.trim() !== '') {
            // Iframe is ready with content
            resolve();
          } else if (attempts >= maxAttempts) {
            // Timeout
            reject(new Error("Cannot access iframe content. The preview might not be ready yet. Please wait a moment and try again."));
          } else {
            // Keep checking
            setTimeout(checkIframeReady, 100);
          }
        };
        
        // Start checking
        checkIframeReady();
      });
      
      const contentDoc = iframe.contentDocument;
      if (!contentDoc || !contentDoc.body) {
        throw new Error("Iframe content is not accessible.");
      }
      
      const report = await checkAllLanguagesForOverflow(
        contentDoc,
        html,
        css,
        languageOptions,
        strings,
        replaceLocaKeys
      );
      
      setOverflowReport(report);
      
      if (report.issues.length === 0) {
        toast({
          title: "All clear!",
          description: "No overflow issues detected in any language.",
        });
      } else {
        toast({
          title: "Issues found",
          description: `Found ${report.issues.length} overflow issue${report.issues.length !== 1 ? 's' : ''} across ${report.languagesWithIssues.size} language${report.languagesWithIssues.size !== 1 ? 's' : ''}.`,
          variant: "default",
        });
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
      toast({
        title: "Error",
        description: `Failed to check for overflow issues: ${errorMessage}`,
        variant: "destructive",
      });
    } finally {
      setIsCheckingOverflow(false);
    }
  }, [html, css, languageOptions, strings, replaceLocaKeys, toast]);

  // Toggle size boxes to visualize constrained elements
  const handleToggleSizeBoxes = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe?.contentDocument) {
      toast({
        title: "Error",
        description: "Preview not ready. Please wait for the preview to load.",
        variant: "destructive",
      });
      return;
    }

    const doc = iframe.contentDocument;
    
    // Check if document body exists and has content
    if (!doc.body) {
      console.error('[Size Boxes] Document body is not available');
      toast({
        title: "Error",
        description: "Preview document body is not available. Please refresh the preview.",
        variant: "destructive",
      });
      return;
    }
    
    console.log('[Size Boxes] Document check:', {
      bodyExists: !!doc.body,
      bodyHTML: doc.body.innerHTML?.substring(0, 200),
      bodyChildren: doc.body.children.length,
      documentReadyState: doc.readyState
    });
    
    const newState = !showSizeBoxes;
    setShowSizeBoxes(newState);

    if (newState) {
      // Add borders to all elements with size constraints
      console.log('[Size Boxes] Starting to scan for elements with size constraints...');
      
      // Wait a moment for the iframe to be fully ready
      setTimeout(() => {
        // Re-get the document to ensure we have a fresh reference
        const freshDoc = iframe.contentDocument;
        if (!freshDoc?.body) {
          console.error('[Size Boxes] Document not available in setTimeout');
          return;
        }
        
        // First, remove any existing size boxes to prevent duplicates
        console.log('[Size Boxes] Cleaning up any existing size boxes first...');
        const existingBoxes = freshDoc.querySelectorAll('[data-size-box]');
        existingBoxes.forEach(element => {
          if (element.nodeType === 1) {
            const htmlElement = element as HTMLElement;
            htmlElement.style.outline = '';
            htmlElement.style.outlineOffset = '';
            htmlElement.removeAttribute('data-size-box');
            
            // Restore original position
            const originalPosition = htmlElement.getAttribute('data-original-position');
            if (originalPosition !== null) {
              htmlElement.style.position = originalPosition || '';
              htmlElement.removeAttribute('data-original-position');
            }
            
            // Remove labels
            const labels = htmlElement.querySelectorAll('.size-box-label');
            labels.forEach(label => label.remove());
          }
        });
        
        console.log('[Size Boxes] Starting element scan...', {
          bodyChildren: freshDoc.body.children.length,
          bodyHTML: freshDoc.body.innerHTML.substring(0, 200)
        });
        
        // Use querySelectorAll to get all elements - more reliable than TreeWalker
        const allElements = freshDoc.querySelectorAll('*');
        const elementsWithConstraints: HTMLElement[] = [];
        let totalElementsScanned = 0;

        console.log(`[Size Boxes] Found ${allElements.length} total elements to scan`);

        allElements.forEach((element) => {
          // Don't use instanceof HTMLElement - it fails across iframe boundaries
          // Just check if it's an element node
          if (element.nodeType === 1) { // ELEMENT_NODE
            totalElementsScanned++;
            const computedStyle = freshDoc.defaultView?.getComputedStyle(element as HTMLElement);
            if (!computedStyle) return;

            const widthValue = parseFloat(computedStyle.width);
            const heightValue = parseFloat(computedStyle.height);

            const hasExplicitWidth = computedStyle.width !== 'auto' && 
                                    !isNaN(widthValue) &&
                                    widthValue > 0 &&
                                    !computedStyle.width.includes('%');
            const hasExplicitHeight = computedStyle.height !== 'auto' && 
                                     !isNaN(heightValue) &&
                                     heightValue > 0 &&
                                     !computedStyle.height.includes('%');
            const hasMaxConstraints = (computedStyle.maxWidth && computedStyle.maxWidth !== 'none') ||
                                     (computedStyle.maxHeight && computedStyle.maxHeight !== 'none');

            if (hasExplicitWidth || hasExplicitHeight || hasMaxConstraints) {
              const htmlElement = element as HTMLElement;
              const tagName = htmlElement.tagName.toLowerCase();
              const className = htmlElement.className || '';
              console.log(`[Size Boxes] Found constrained element: ${tagName}.${className}`, {
                width: computedStyle.width,
                height: computedStyle.height,
                widthValue,
                heightValue,
                hasExplicitWidth,
                hasExplicitHeight,
                hasMaxConstraints
              });
              elementsWithConstraints.push(htmlElement);
              
              // Add border with high visibility
              htmlElement.style.outline = '3px solid #3b82f6';
              htmlElement.style.outlineOffset = '0px';
              htmlElement.setAttribute('data-size-box', 'true');
              
              // Store original position to restore later
              const originalPosition = htmlElement.style.position;
              htmlElement.setAttribute('data-original-position', originalPosition || '');
              
              // Make element positioned if not already (for label positioning)
              const position = computedStyle.position;
              if (position === 'static') {
                htmlElement.style.position = 'relative';
              }
              
              // Add size label as overlay
              const label = freshDoc.createElement('div');
              label.className = 'size-box-label';
              const classNameForLabel = htmlElement.className ? `.${htmlElement.className.split(' ')[0]}` : '';
              label.textContent = `${tagName}${classNameForLabel}: ${Math.round(widthValue)}×${Math.round(heightValue)}px`;
              label.style.cssText = `
                position: absolute;
                top: 2px;
                left: 2px;
                background: rgba(59, 130, 246, 0.95);
                color: white;
                padding: 4px 8px;
                border-radius: 4px;
                font-size: 11px;
                font-weight: bold;
                z-index: 999999;
                pointer-events: none;
                white-space: nowrap;
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
                box-shadow: 0 2px 4px rgba(0,0,0,0.3);
              `;
              
              htmlElement.appendChild(label);
            }
          }
        });

        console.log(`[Size Boxes] Scan complete - Scanned ${totalElementsScanned} elements, found ${elementsWithConstraints.length} with size constraints`);
      }, 100); // Wait 100ms for iframe to be ready
      
    } else {
      // Remove all size box borders and labels
      setTimeout(() => {
        // Re-get the document to ensure we have a fresh reference
        const freshDoc = iframe.contentDocument;
        if (!freshDoc?.body) {
          console.error('[Size Boxes] Document not available for cleanup');
          return;
        }
        
        const elements = freshDoc.querySelectorAll('[data-size-box]');
        let cleanedCount = 0;
        elements.forEach(element => {
          if (element.nodeType === 1) { // ELEMENT_NODE
            const htmlElement = element as HTMLElement;
            htmlElement.style.outline = '';
            htmlElement.style.outlineOffset = '';
            htmlElement.removeAttribute('data-size-box');
            
            // Restore original position
            const originalPosition = htmlElement.getAttribute('data-original-position');
            if (originalPosition !== null) {
              htmlElement.style.position = originalPosition || '';
              htmlElement.removeAttribute('data-original-position');
            }
            
            // Remove labels
            const labels = htmlElement.querySelectorAll('.size-box-label');
            labels.forEach(label => label.remove());
            cleanedCount++;
          }
        });
        
        console.log(`[Size Boxes] Removed ${cleanedCount} size box highlights`);
      }, 10); // Small delay to ensure document is accessible
    }
  }, [showSizeBoxes, toast]);

  const handleClear = () => {
    setHtml("");
    setCss("");
    setEditingTemplateId(null);
    setEditingTemplateName("");
  };

  const handleReset = () => {
    setHtml(`<div class="container">
  <button class="btn">{t('platform.pages.uipage.click_me_button_2')}</button>
</div>`);
    setCss(`.container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
}

.btn {
  width: 200px;
  height: 40px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  font-size: 16px;
  border-radius: 8px;
  cursor: pointer;
  transition: transform 0.2s;
}

.btn:hover {
  transform: scale(1.05);
}`);
    setEditingTemplateId(null);
    setEditingTemplateName("");
  };

  return (
    <div className="flex flex-col h-[calc(100vh-3rem)] bg-background">
    
       
      <div className="flex-1 flex flex-col p-6 gap-4 overflow-hidden min-h-0">
        {/* Info Bar */}
        <div className="flex items-center justify-between shrink-0 p-3 bg-muted/30 rounded-lg border border-input">
          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            <div className="flex items-center gap-2">
              <IconProvider iconstring="fa-language" size={14} />
              <span>{languageOptions.length - 1} languages available</span>
            </div>
            <div className="flex items-center gap-2">
              <IconProvider iconstring="fa-database" size={14} />
              <span>{strings.length} LocaKeys loaded</span>
            </div>
            <div className="flex items-center gap-2">
              <IconProvider iconstring="fa-image" size={14} />
              <span>{images.length} Images available</span>
            </div>
            <div className="text-xs">
              <span className="font-medium">{t('platform.pages.uipage.locakeys')}</span> <code className="px-1 py-0.5 bg-muted rounded">{'{{key}}'}</code> • 
              <span className="font-medium ml-2">{t('platform.pages.uipage.images')}</span> <code className="px-1 py-0.5 bg-muted rounded">{'{{img:image-key}}'}</code>
            </div>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleQuickSave}
              className="gap-2 h-7 text-xs"
              title={editingTemplateId ? `Update "${editingTemplateName}"` : "Save as new template"}
            >
              <Save size={12} />
              Save{editingTemplateId ? ` "${editingTemplateName}"` : ""}
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleSaveAs}
              className="gap-2 h-7 text-xs"
              title={t('platform.pages.uipage.save_as_a_new_template_or_with_a_different_name')}
            >
              <IconProvider iconstring="fa-save" size={12} />
              Save As...
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setIsLoadDialogOpen(true)}
              className="gap-2 h-7 text-xs"
              disabled={uiElements.length === 0}
            >
              <FolderOpen size={12} />
              Load ({uiElements.length})
            </Button>
            <div className="h-4 w-px bg-border" />
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setIsUnityExportDialogOpen(true)}
              className="gap-2 h-7 text-xs bg-blue-500/10 hover:bg-blue-500/20 border-blue-500/30"
              title={t('platform.pages.uipage.export_ui_for_unity_ui_toolkit')}
            >
              <IconProvider iconstring="fa-unity" size={12} />
              Export for Unity
            </Button>
            <div className="h-4 w-px bg-border" />
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleClear}
              className="gap-2 h-7 text-xs"
            >
              <IconProvider iconstring="fa-eraser" size={12} />
              Clear
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleReset}
              className="gap-2 h-7 text-xs"
            >
              <IconProvider iconstring="fa-rotate-right" size={12} />
              Reset
            </Button>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 flex gap-4 overflow-hidden min-h-0">
          {/* Editors Column */}
          <div className="flex-1 flex flex-col gap-4 overflow-hidden min-h-0">
            {/* Overflow Detection Panel */}
            <div className="shrink-0">
              <OverflowReportPanel
                report={overflowReport}
                isChecking={isCheckingOverflow}
                onCheck={handleCheckOverflow}
                currentLanguage={selectedLanguage}
                onLanguageSelect={setSelectedLanguage}
              />
            </div>
            {/* HTML Editor */}
            <div className="flex-1 flex flex-col border border-input rounded overflow-hidden bg-card min-h-0">
              <div className="flex items-center gap-2 px-4 py-2 border-b border-input bg-muted/50 shrink-0">
                <span className="text-sm font-medium">HTML</span>
              </div>
              <div className="flex-1 overflow-auto min-h-0">
                <Editor
                  value={html}
                  onValueChange={setHtml}
                  highlight={(code) => highlight(code, languages.markup, 'markup')}
                  padding={16}
                  style={{
                    fontFamily: '"Fira code", "Fira Mono", monospace',
                    fontSize: 14,
                    minHeight: '100%',
                    backgroundColor: 'hsl(var(--background))',
                    color: 'hsl(var(--foreground))',
                  }}
                  textareaClassName="outline-none"
                  placeholder={t('platform.pages.uipage.enter_your_html_here')}
                />
              </div>
            </div>

            {/* CSS Editor */}
            <div className="flex-1 flex flex-col border border-input rounded overflow-hidden bg-card min-h-0">
              <div className="flex items-center gap-2 px-4 py-2 border-b border-input bg-muted/50 shrink-0">
                <span className="text-sm font-medium">CSS</span>
              </div>
              <div className="flex-1 overflow-auto min-h-0">
                <Editor
                  value={css}
                  onValueChange={setCss}
                  highlight={(code) => highlight(code, languages.css, 'css')}
                  padding={16}
                  style={{
                    fontFamily: '"Fira code", "Fira Mono", monospace',
                    fontSize: 14,
                    minHeight: '100%',
                    backgroundColor: 'hsl(var(--background))',
                    color: 'hsl(var(--foreground))',
                  }}
                  textareaClassName="outline-none"
                  placeholder={t('platform.pages.uipage.enter_your_css_here')}
                />
              </div>
            </div>
          </div>

          {/* Preview Column */}
          <div className="flex-1 flex flex-col border border-input rounded overflow-hidden bg-card min-h-0">
            <div className="flex items-center justify-between gap-2 px-4 py-2 border-b border-input bg-muted/50 shrink-0">
              <div className="flex items-center gap-2">
                <IconProvider iconstring="fa-eye" size={16} className="text-green-500" />
                <span className="text-sm font-medium">{t('platform.common.preview')}</span>
              </div>
              <div className="flex items-center gap-3">
                {/* Size Boxes Toggle Button */}
                <Button
                  variant={showSizeBoxes ? "default" : "outline"}
                  size="sm"
                  onClick={handleToggleSizeBoxes}
                  className="gap-2 h-7 text-xs"
                  title={t('platform.pages.uipage.showhide_size_boxes_for_constrained_elements')}
                >
                  <IconProvider iconstring="fa-ruler-combined" size={12} />
                  {showSizeBoxes ? 'Hide' : 'Show'} Sizes
                </Button>
                {/* Language Selector */}
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">{t('platform.pages.uipage.language')}</span>
                  <Select value={selectedLanguage} onValueChange={setSelectedLanguage}>
                    <SelectTrigger className="w-[180px] h-8 text-xs">
                      <SelectValue placeholder={t('platform.pages.uipage.select_language')}>
                        {languageOptions.find(lang => lang.code === selectedLanguage)?.name || 'Select language'}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {languageOptions.length > 0 ? (
                        languageOptions.map((lang) => (
                          <SelectItem key={lang.code} value={lang.code} className="text-xs">
                            {lang.name}
                          </SelectItem>
                        ))
                      ) : (
                        <SelectItem value="none" disabled className="text-xs">
                          No languages configured
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <div className="flex-1 overflow-auto bg-white min-h-0 relative" style={{ minHeight: '400px' }}>
              {previewContent ? (
                iframeError ? (
                  <div className="p-4">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                      <h3 className="text-red-800 font-medium mb-2">{t('platform.pages.uipage.iframe_failed_to_load')}</h3>
                      <p className="text-red-600 text-sm mb-4">{t('platform.pages.uipage.the_preview_iframe_encountered_an_error_heres_the_raw_html_content')}</p>
                      <pre className="bg-gray-100 p-3 rounded text-xs overflow-auto max-h-64">
                        {previewContent}
                      </pre>
                    </div>
                  </div>
                ) : (
                  <iframe
                    key={previewContent.length} // Force re-render when content changes
                    ref={iframeRef}
                    srcDoc={previewContent}
                    className="w-full h-full border-0"
                    title={t('platform.common.preview')}
                    onLoad={() => {
                      // console.log('Iframe loaded, content:', previewContent);
                      if (iframeRef.current?.contentDocument) {
                        // console.log('Iframe document body:', iframeRef.current.contentDocument.body?.innerHTML);
                      }
                      setIframeError(false); // Reset error state on successful load
                    }}
                    onError={(e) => {
                      console.error('Iframe error:', e);
                      setIframeError(true);
                    }}
                  />
                )
              ) : (
                <div className="flex items-center justify-center h-full text-muted-foreground">
                  <div className="text-center">
                    <p>{t('platform.pages.uipage.no_content_to_preview')}</p>
                    <p className="text-xs mt-2">{t('platform.pages.uipage.add_some_html_to_see_the_preview')}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Save Template Dialog */}
      <Dialog open={isSaveDialogOpen} onOpenChange={setIsSaveDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{t('platform.pages.uipage.save_ui_template')}</DialogTitle>
            <DialogDescription>
              {editingTemplateId 
                ? `Currently editing "${editingTemplateName}". Use the same name to update it, or enter a new name to save as a copy.`
                : "Give your template a name to save the current HTML and CSS"
              }
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="template-name">{t('platform.pages.uipage.template_name')}</Label>
              <Input
                id="template-name"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder={t('platform.pages.uipage.eg_button_component_card_layout')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    saveTemplate();
                  }
                }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsSaveDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={saveTemplate}>
              {editingTemplateId && templateName.trim() === editingTemplateName 
                ? "Update Template" 
                : "Save Template"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Load Template Dialog */}
      <Dialog open={isLoadDialogOpen} onOpenChange={setIsLoadDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>{t('platform.pages.uipage.load_ui_template')}</DialogTitle>
            <DialogDescription>
              Select a saved template to load
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[400px] overflow-y-auto">
            {uiElementsLoading ? (
              <div className="text-center py-8 text-muted-foreground">
                Loading templates...
              </div>
            ) : uiElements.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No saved templates yet
              </div>
            ) : (
              <div className="space-y-2">
                {uiElements.map((element) => (
                  <div
                    key={element.id}
                    className="flex items-center justify-between p-3 border border-input rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-sm truncate">{element.name}</h4>
                      <p className="text-xs text-muted-foreground">
                        Saved {new Date(element.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex gap-2 ml-4">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => loadTemplate(element)}
                        className="h-8"
                      >
                        <FolderOpen size={14} className="mr-1" />
                        Load
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => deleteTemplate(element.id)}
                        className="h-8 text-destructive hover:text-destructive"
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button 
              variant="default" 
              onClick={() => {
                setIsLoadDialogOpen(false);
                setIsUnityExportDialogOpen(true);
              }}
              className="gap-2"
            >
              <IconProvider iconstring="fa-unity" size={14} />
              Export for Unity
            </Button>
            <Button variant="outline" onClick={() => setIsLoadDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unity Export Dialog */}
      <UnityExportDialog
        open={isUnityExportDialogOpen}
        onOpenChange={setIsUnityExportDialogOpen}
        html={html}
        css={css}
        images={images}
        templateName={editingTemplateName || 'UI'}
      />
    </div>
  );
}

